/**
 * Каркас приложения: вход, навигация, полоска синхронизации.
 *
 * Экранов пять (раздел 8). Нижняя панель показывает четыре — активная
 * тренировка открывается с экрана «Сегодня» и панель прячет: между
 * подходами промахнуться мимо кнопки нельзя.
 */

import { useEffect, useState } from 'react'
import { Link, NavLink, Route, Routes, useLocation } from 'react-router-dom'

import { ApiError, NetworkError, api } from './api/client'
import type { AuthResult } from './api/types'
import { ensureSession, isMiniApp } from './auth/telegram'
import { hasToken, setToken } from './auth/session'
import { Loader } from './components/ui'
import { startAutoSync, subscribe, retryFailed, type QueueState } from './offline/queue'
import Install from './screens/Install'
import Plan from './screens/Plan'
import Programs from './screens/Programs'
import SessionEdit from './screens/SessionEdit'
import Today from './screens/Today'
import Workout from './screens/Workout'
import Progress from './screens/Progress'
import Goals from './screens/Goals'
import History from './screens/History'

type AuthState = 'checking' | 'ready' | 'anonymous' | 'error'

function SyncBar() {
  const [state, setState] = useState<QueueState | null>(null)

  useEffect(() => subscribe(setState), [])
  useEffect(() => startAutoSync(), [])

  if (!state) return null
  if (state.failed > 0) {
    return (
      <div className="syncbar failed" onClick={() => void retryFailed()}>
        {state.failed} подх. не приняты сервером — нажми, чтобы повторить
      </div>
    )
  }
  if (state.pending > 0) {
    return (
      <div className="syncbar pending">
        {state.online ? 'Отправляю' : 'Нет связи'} · {state.pending} подх. ждут отправки
      </div>
    )
  }
  return null
}

function TabBar() {
  const tabs = [
    { to: '/', glyph: '🏋', label: 'Сегодня', end: true },
    { to: '/plan', glyph: '📋', label: 'Программа', end: false },
    { to: '/progress', glyph: '📈', label: 'Прогресс', end: false },
    { to: '/goals', glyph: '🎯', label: 'Цели', end: false },
    { to: '/history', glyph: '🗒', label: 'История', end: false },
  ]
  return (
    <nav className="tabbar">
      {tabs.map((t) => (
        <NavLink key={t.to} to={t.to} end={t.end} className={({ isActive }) => (isActive ? 'active' : '')}>
          <span className="glyph">{t.glyph}</span>
          <span>{t.label}</span>
        </NavLink>
      ))}
    </nav>
  )
}

/**
 * Достать токен из того, что человек вставил.
 *
 * Принимается и полная ссылка, и голый токен: из Telegram копируется ссылка
 * целиком, но человек может прислать себе и обрывок. Требовать точный формат
 * от того, кто уже возится с буфером обмена на телефоне, — лишняя строгость.
 */
const TOKEN_SHAPE = /^[A-Za-z0-9_\-=.]+$/

export function tokenFromPaste(raw: string): string | null {
  const text = raw.trim()
  if (!text) return null

  const marker = '/api/auth/magic/'
  const at = text.indexOf(marker)
  const tail = at >= 0 ? text.slice(at + marker.length) : text
  const token = tail.split(/[?#\s/]/)[0]

  // Форма проверяется до отправки, и не ради красоты: строка подставляется в
  // ПУТЬ запроса, а «..» или «/» в ней увели бы запрос на соседнюю ручку.
  // Токен — это base64url и точка, больше в нём ничего нет.
  return token && TOKEN_SHAPE.test(token) ? token : null
}

/**
 * Экран для случая «открыли в браузере без токена».
 *
 * Здесь же вход по вставленной ссылке. Он нужен не для удобства: ярлык на
 * домашнем экране iOS держит СВОЁ хранилище, отдельное от Safari, и ссылку
 * из чата в него не открыть — iOS не умеет передавать ссылку установленному
 * веб-приложению. Без поля вставки в ярлык нельзя войти вообще.
 */
function NeedLogin({ onDone }: { onDone: () => void }) {
  const [paste, setPaste] = useState('')
  const [busy, setBusy] = useState(false)
  const [failure, setFailure] = useState('')

  async function submit(): Promise<void> {
    const token = tokenFromPaste(paste)
    if (!token) {
      setFailure('Вставь ссылку целиком — ту, что прислал бот на команду /login.')
      return
    }
    setBusy(true)
    setFailure('')
    try {
      const result = await api.get<AuthResult>(`/auth/magic/${token}`, { anonymous: true })
      if (typeof result?.token !== 'string' || !result.token) {
        throw new Error('Сервер ответил не тем, чего мы ждали. Попробуй ещё раз.')
      }
      setToken(result.token)

      // Перезагрузки здесь быть НЕ должно, и это не мелочь. В приватном
      // режиме Safari localStorage закрыт, и токен живёт только в памяти
      // вкладки: перезагрузка стёрла бы его вместе с ней, экран снова
      // попросил бы вход, а ссылка уже одноразово потрачена. Человек
      // крутился бы по кругу, сжигая ссылку за ссылкой.
      onDone()
    } catch (e) {
      setFailure(
        e instanceof NetworkError
          ? 'Нет связи с сервером. Ссылка не потрачена — попробуй ещё раз.'
          : e instanceof ApiError
            ? `${e.message}. Ссылка живёт несколько минут и срабатывает один раз — попроси новую командой /login.`
            : 'Ссылка не принята. Попроси новую командой /login.',
      )
    } finally {
      setBusy(false)
    }
  }

  return (
    <div className="screen">
      <h1>Нужен вход</h1>
      <p className="muted">
        Напиши боту команду <b>/login</b> — он пришлёт ссылку. Нажми и удерживай её,
        выбери «Скопировать» и вставь сюда.
      </p>

      <input
        className="se-search"
        type="text"
        value={paste}
        onChange={(e) => setPaste(e.target.value)}
        onKeyDown={(e) => {
          // На iOS клавиатура показывает «Go», и нажать его естественнее,
          // чем тянуться к кнопке под полем.
          if (e.key === 'Enter' && !busy) void submit()
        }}
        placeholder="Вставь ссылку из бота"
        autoComplete="off"
        autoCapitalize="off"
        spellCheck={false}
      />
      {failure && <div className="se-fail">{failure}</div>}
      <button className="btn wide primary" disabled={busy} onClick={() => void submit()}>
        {busy ? 'Вхожу…' : 'Войти по ссылке'}
      </button>

      <p className="faint" style={{ marginTop: 16 }}>
        Ссылка живёт несколько минут и срабатывает один раз. Если открыть её в Telegram,
        она сгорит — поэтому копируй, а не нажимай.
      </p>

      <Link className="btn wide ghost" to="/install">
        Как поставить на экран «Домой»
      </Link>
    </div>
  )
}

export default function App() {
  const [auth, setAuth] = useState<AuthState>('checking')
  const [authError, setAuthError] = useState<string>('')
  const location = useLocation()

  useEffect(() => {
    let cancelled = false
    ensureSession()
      .then((ok) => {
        if (cancelled) return
        setAuth(ok ? 'ready' : 'anonymous')
      })
      .catch((e: unknown) => {
        if (cancelled) return
        setAuthError(e instanceof Error ? e.message : 'Не удалось войти')
        // В Mini App вход обязан был сработать — показываем причину.
        // В браузере это ожидаемо: там вход по ссылке из бота.
        setAuth(isMiniApp() ? 'error' : 'anonymous')
      })
    return () => {
      cancelled = true
    }
  }, [])

  if (auth === 'checking') {
    return (
      <div className="screen">
        <Loader label="Вхожу…" />
      </div>
    )
  }

  if (auth === 'error') {
    return (
      <div className="screen">
        <h1>Не удалось войти</h1>
        <div className="card" style={{ color: 'var(--danger)' }}>{authError}</div>
        <button className="btn wide" onClick={() => window.location.reload()}>
          Попробовать снова
        </button>
      </div>
    )
  }

  if (auth === 'anonymous' && !hasToken()) {
    // Инструкция по установке доступна и без входа — именно там она и нужна:
    // человек ставит ярлык до того, как войдёт. Всё остальное без токена
    // показывать нечего.
    return (
      <Routes>
        <Route path="/install" element={<Install />} />
        <Route path="*" element={<NeedLogin onDone={() => setAuth('ready')} />} />
      </Routes>
    )
  }

  // На активной тренировке панель убирается: экран занят подходами.
  const inWorkout = location.pathname.startsWith('/workout/')

  return (
    <>
      <SyncBar />
      <Routes>
        <Route path="/" element={<Today />} />
        <Route path="/workout/:sessionId" element={<Workout />} />
        <Route path="/plan" element={<Plan />} />
        <Route path="/plan/:sessionId" element={<SessionEdit />} />
        <Route path="/programs" element={<Programs />} />
        <Route path="/install" element={<Install />} />
        <Route path="/progress" element={<Progress />} />
        <Route path="/goals" element={<Goals />} />
        <Route path="/history" element={<History />} />
        <Route path="*" element={<Today />} />
      </Routes>
      {!inWorkout && <TabBar />}
    </>
  )
}
